import { useRef, useEffect } from "react";
import { Box, Typography, Container } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

import { SEO } from "../../components";
import THEME from "../../constants/theme";
import PageTitle from "../../components/common/PageTitle";
import OfficeMap from "../../components/pages/contacts/OfficeMap";

const ContactsPage = ({ setRefs }) => {
  const contactsRef = useRef(null);

  useEffect(() => {
    setRefs({ contacts: contactsRef });
  }, [setRefs]);

  return (
    <>
      <SEO
        title="Contacts"
        description="Contact OctopusSync for inquiries about data synchronization and operational tooling."
        keywords="contact, OctopusSync, data sync, operations, London"
        canonical="/contacts"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "OctopusSync",
          "url": "https://octopus-sync.com",
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "support@sentinel-risk.co.uk",
            "contactType": "customer support",
          }
        }}
      />
      <Box
        ref={contactsRef}
        id="contacts"
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, md: 8 },
          gap: 4,
          width: "100%"
        }}
      >
        <Container maxWidth="xl" sx={{ width: "100%" }}>
          <PageTitle title="Contact Us" subtitle="Get in touch with the OctopusSync team" sx={{ mb: 6 }} />

          {/* Contact Info Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              justifyContent: "center",
              mb: 6
            }}
          >
            {/* Office Location */}
            <Box
              sx={{
                p: 3,
                flex: 1,
                background: "linear-gradient(135deg, rgba(101,17,175,0.08) 0%, rgba(101,17,175,0.04) 100%)",
                border: "1px solid rgba(101,17,175,0.14)",
                borderRadius: 3,
                boxShadow: "0 12px 35px rgba(0,0,0,0.08)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 800,
                  color: THEME.COLORS.PRIMARY_PURPLE,
                  letterSpacing: 0.3
                }}
              >
                Office Location
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <LocationOnIcon sx={{ fontSize: "1.6rem", color: THEME.COLORS.PRIMARY_PURPLE, mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" sx={{ color: THEME.COLORS.NEUTRAL_BLACK, fontWeight: 600, lineHeight: 1.7 }}>
                    Capital Office, 124 City Road
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME.COLORS.NEUTRAL_GRAY_DARK }}>
                    London, EC1V 2NX, England
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Email */}
            <Box
              sx={{
                p: 3,
                flex: 1,
                background: "linear-gradient(135deg, rgba(101,17,175,0.08) 0%, rgba(101,17,175,0.04) 100%)",
                border: "1px solid rgba(101,17,175,0.14)",
                borderRadius: 3,
                boxShadow: "0 12px 35px rgba(0,0,0,0.08)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 800,
                  color: THEME.COLORS.PRIMARY_PURPLE,
                  letterSpacing: 0.3
                }}
              >
                Email Us
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <EmailIcon sx={{ fontSize: "1.6rem", color: THEME.COLORS.PRIMARY_PURPLE }} />
                <Box>
                  <Typography variant="caption" sx={{ color: THEME.COLORS.PRIMARY_PURPLE, fontWeight: 700, display: "block", fontSize: "0.85rem" }}>
                    General Support
                  </Typography>
                  <Typography
                    component="a"
                    href="mailto:support@sentinel-risk.co.uk"
                    variant="body2"
                    sx={{
                      color: THEME.COLORS.NEUTRAL_BLACK,
                      fontSize: "1rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      "&:hover": { color: THEME.COLORS.PRIMARY_PURPLE }
                    }}
                  >
                    support@sentinel-risk.co.uk
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Phone */}
            <Box
              sx={{
                p: 3,
                flex: 1,
                background: "linear-gradient(135deg, rgba(101,17,175,0.08) 0%, rgba(101,17,175,0.04) 100%)",
                border: "1px solid rgba(101,17,175,0.14)",
                borderRadius: 3,
                boxShadow: "0 12px 35px rgba(0,0,0,0.08)"
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 800,
                  color: THEME.COLORS.PRIMARY_PURPLE,
                  letterSpacing: 0.3
                }}
              >
                Call Us
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <LocalPhoneIcon sx={{ fontSize: "1.6rem", color: THEME.COLORS.PRIMARY_PURPLE }} />
                <Box>
                  <Typography variant="body1" sx={{ color: THEME.COLORS.NEUTRAL_BLACK, fontSize: "1.1rem", fontWeight: 700 }}>
                    +44 20 4587 9699
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME.COLORS.NEUTRAL_GRAY_DARK }}>
                    Mon–Fri, 8:00–17:00 UK
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Map Section - Full Width */}
          <Box
            sx={{
              width: "100%",
              height: { xs: "40vh", md: "55vh", lg: "60vh" },
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
              overflow: "hidden",
              mt: 2
            }}
          >
            <OfficeMap height="100%" />
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ContactsPage;
